/*
 * Client executor for guarded Zana admin actions.
 *
 * Public intents are deliberately PII-free. Account rows and invite emails are
 * resolved only in this authorized client context and must never be copied into
 * Ask Zana history or a model prompt.
 */

import { auth } from '../firebase';

export type ZanaAdminActionId =
  | 'list_unsigned_accounts'
  | 'contact_late_bus_parents'
  | 'prepare_school_trip_draft'
  | 'export_monthly_report'
  | 'invite_driver';

export interface ZanaAdminIntent {
  actionId: ZanaAdminActionId;
  params: Record<string, string>;
  confirmRequired?: boolean;
}

export interface UnsignedAccount {
  accountRef: string;
  email: string;
  role: 'parent' | 'bus_driver';
  createdAt: string | null;
}

export interface ZanaAdminResult {
  ok: boolean;
  message: string;
  /** Authorized, client-only rows. Never append this field to Zana chat. */
  unsignedAccounts?: UnsignedAccount[];
  draftId?: string;
}

export interface ZanaAdminExecutionContext {
  orgCode: string;
  confirmed: boolean;
  /** Existing Monthly Report CSV flow; receives only the allow-listed month. */
  exportMonthlyReport: (month: string, format: 'csv') => void | Promise<void>;
  /** Resolve an opaque reference from an authorized, client-private map. */
  resolveInviteEmail: (inviteRef: string) => string | null;
  /** Existing sendFleetInvite(..., role='bus_driver') flow. */
  sendDriverInvite: (email: string) => Promise<{ ok: boolean; error?: string }>;
}

const BASE = (process.env.REACT_APP_PAYMENTS_URL || '').replace(/\/$/, '');
const WRITE_IDS = new Set<ZanaAdminActionId>([
  'contact_late_bus_parents',
  'prepare_school_trip_draft',
  'invite_driver',
]);
const ACTION_IDS = new Set<ZanaAdminActionId>([
  'list_unsigned_accounts',
  'contact_late_bus_parents',
  'prepare_school_trip_draft',
  'export_monthly_report',
  'invite_driver',
]);
const EMAIL_RE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
const EMAIL_FULL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const PHONE_RE = /(?:^|[^\d])(?:\+?\d[\d\s().-]{6,}\d)(?:$|[^\d])/;
const SAFE_REF_RE = /^[A-Za-z0-9][A-Za-z0-9_.:-]*$/;

function cleanText(value: unknown, max: number): string {
  return String(value ?? '')
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/[<>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}

export function containsDirectPii(value: unknown): boolean {
  const text = String(value ?? '');
  return EMAIL_RE.test(text) || PHONE_RE.test(text);
}

function safeRef(value: unknown, max = 80): string {
  const ref = String(value ?? '').trim().slice(0, max);
  return ref && SAFE_REF_RE.test(ref) && !containsDirectPii(ref) ? ref : '';
}

function normalizePlate(value: unknown): string {
  const plate = String(value ?? '').toUpperCase().replace(/[\s-]/g, '').slice(0, 16);
  return /^[A-Z0-9]{3,16}$/.test(plate) ? plate : '';
}

function isoDate(value: unknown): string {
  const date = String(value ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return '';
  const parsed = new Date(`${date}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date ? date : '';
}

function monthKey(value: unknown): string {
  const month = String(value ?? '').trim();
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(month) ? month : '';
}

export function sanitizeZanaAdminIntent(raw: unknown): ZanaAdminIntent | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as { actionId?: unknown; action?: unknown; params?: unknown };
  const actionId = String(item.actionId || item.action || '') as ZanaAdminActionId;
  if (!ACTION_IDS.has(actionId)) return null;
  const src = item.params && typeof item.params === 'object'
    ? item.params as Record<string, unknown>
    : raw as Record<string, unknown>;
  const params: Record<string, string> = {};

  if (actionId === 'contact_late_bus_parents') {
    const plate = normalizePlate(src.plate || src.busId);
    const message = cleanText(src.message, 500);
    if (!plate || !message || containsDirectPii(message)) return null;
    params.plate = plate;
    params.message = message;
  } else if (actionId === 'prepare_school_trip_draft') {
    const destination = cleanText(src.destination, 120);
    const date = isoDate(src.date);
    const driverId = safeRef(src.driverId, 80);
    if (!destination || containsDirectPii(destination) || !date || !driverId) return null;
    params.destination = destination;
    params.date = date;
    params.driverId = driverId;
  } else if (actionId === 'export_monthly_report') {
    const month = monthKey(src.month);
    if (!month || String(src.format || 'csv').toLowerCase() !== 'csv') return null;
    params.month = month;
    params.format = 'csv';
  } else if (actionId === 'invite_driver') {
    const inviteRef = safeRef(src.inviteRef, 96);
    if (!inviteRef) return null;
    params.inviteRef = inviteRef;
  }

  return {
    actionId,
    params,
    ...(WRITE_IDS.has(actionId) ? { confirmRequired: true } : {}),
  };
}

async function postServerAction(
  orgCode: string,
  intent: ZanaAdminIntent,
  confirmed: boolean,
): Promise<Record<string, unknown>> {
  if (!BASE) throw new Error('Payments backend is not configured.');
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Sign in again before running this action.');
  const response = await fetch(`${BASE}/api/premium/zana/admin-action`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ orgCode, ...intent, confirmed }),
  });
  const json = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok || json.ok !== true) {
    throw new Error(String(json.message || 'The action could not be completed.'));
  }
  return json;
}

/** Execute only a previously-sanitized, explicitly confirmed action. */
export async function executeZanaAdminIntent(
  rawIntent: unknown,
  context: ZanaAdminExecutionContext,
): Promise<ZanaAdminResult> {
  const intent = sanitizeZanaAdminIntent(rawIntent);
  if (!intent) return { ok: false, message: 'That admin action is not allowed.' };
  if (!context.orgCode) return { ok: false, message: 'Organization context is missing.' };
  if (WRITE_IDS.has(intent.actionId) && context.confirmed !== true) {
    return { ok: false, message: 'Confirm this action before it runs.' };
  }

  try {
    if (intent.actionId === 'export_monthly_report') {
      await context.exportMonthlyReport(intent.params.month, 'csv');
      return { ok: true, message: `Downloaded the ${intent.params.month} monthly report.` };
    }

    if (intent.actionId === 'invite_driver') {
      const email = context.resolveInviteEmail(intent.params.inviteRef);
      if (!email || !EMAIL_FULL_RE.test(email)) {
        return { ok: false, message: 'That private driver selection expired. Choose the driver again.' };
      }
      const result = await context.sendDriverInvite(email);
      return result.ok
        ? { ok: true, message: 'Driver invite sent.' }
        : { ok: false, message: result.error || 'The driver invite could not be sent.' };
    }

    const json = await postServerAction(context.orgCode, intent, context.confirmed);
    if (intent.actionId === 'list_unsigned_accounts') {
      const rows = Array.isArray(json.accounts) ? json.accounts as UnsignedAccount[] : [];
      return {
        ok: true,
        message: rows.length === 1
          ? '1 account has not completed signup.'
          : `${rows.length} accounts have not completed signup.`,
        unsignedAccounts: rows,
      };
    }
    if (intent.actionId === 'contact_late_bus_parents') {
      const sent = Number(json.sent) || 0;
      const total = Number(json.total) || 0;
      return { ok: true, message: `Sent the late-bus update to ${sent} of ${total} parent accounts.` };
    }
    return {
      ok: true,
      message: 'School trip draft saved. It has not notified parents or started a trip.',
      draftId: typeof json.draftId === 'string' ? json.draftId : undefined,
    };
  } catch (error) {
    return {
      ok: false,
      message: error instanceof Error ? error.message : 'The action could not be completed.',
    };
  }
}

/**
 * Private email map for a selected driver. Only the opaque reference belongs in
 * a Zana intent; the email is consumed locally by sendFleetInvite after confirm.
 */
export class ZanaDriverInviteSelections {
  private readonly emails = new Map<string, string>();

  add(email: string): string {
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_FULL_RE.test(normalized)) throw new Error('A valid driver email is required.');
    const random = typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function'
      ? crypto.getRandomValues(new Uint32Array(3))
      : new Uint32Array([Date.now(), Math.random() * 0xffffffff, Math.random() * 0xffffffff]);
    const ref = `driver-invite:${Array.from(random).map((n) => Math.floor(n).toString(36)).join('')}`;
    this.emails.set(ref, normalized);
    return ref;
  }

  resolve = (ref: string): string | null => this.emails.get(ref) || null;

  consume(ref: string): string | null {
    const email = this.resolve(ref);
    this.emails.delete(ref);
    return email;
  }
}
